import { toast } from 'react-toastify';

import SDK from '../../Components/SDK';
import { getConnectionStatus } from '../../Components/WebChat/Common/FileUploadValidation';
import logger from '../../Helpers/logger';
import { i18n } from '../../localization/i18n';
import { getAllItems, getItem, KEY_VALUE_STORE, removeItem, setItem } from '../../services/keyValueStore';
import { OUTBOX_STATUS, removeFromOutbox, setOutboxStatus, setOutboxStatuses } from '../../slice/outboxSlice';
import { refuseIfSendBlocked } from '../chatDetail/Composer/sendPermission';
import { POLL_ERROR } from '../poll/pollRules';
import { markPollSent } from '../poll/pollSent';

export const OUTBOX_KIND = {
    TEXT: 'text',
    CONTACT: 'contact',
    POLL: 'poll',
};

const OUTBOX_STORE = KEY_VALUE_STORE.MESSAGE_OUTBOX;
const CONNECTED = 'CONNECTED';
// SDK send answer when the stanza went into its own offline queue.
const SDK_QUEUED_MESSAGE = 'Network Error';
// SDK REST answer for a request that timed out or never reached the server.
const SDK_TIMEOUT_STATUS = 599;
const POLL_RETRY_BASE_MS = 5000;
const POLL_RETRY_MAX_MS = 60000;

// Handed to the SDK during this page load; its in-memory queue dies with the page.
const handedOffMsgIds = new Set();
let isFlushing = false;
let isFlushRequested = false;
let pollRetryTimer = null;
let pollRetryDelayMs = 0;

/**
 * Sends every queued message, oldest first. Stops while disconnected and resumes on the
 * next flush, which the login and reconnect sync trigger.
 */
export const flushOutbox = () => async dispatch => {
    // A send arriving mid-flush gets one more pass, not a parallel one.
    if (isFlushing) {
        isFlushRequested = true;
        return;
    }
    isFlushing = true;
    try {
        do {
            isFlushRequested = false;
            await sendQueuedMessages(dispatch);
        } while (isFlushRequested);
    } finally {
        isFlushing = false;
    }
};

/**
 * Queues messages already rendered as Pending, then tries to send them in order.
 *
 * @param {object[]} items { msgId, kind: OUTBOX_KIND, params } in send order; params are
 * the arguments of the matching SDK send call.
 */
export const enqueueOutboxMessages = items => async dispatch => {
    const createdAt = Date.now();
    await Promise.all(
        items.map((item, seq) =>
            setItem(OUTBOX_STORE, item.msgId, { ...item, seq, createdAt, status: OUTBOX_STATUS.QUEUED }),
        ),
    );
    dispatch(setOutboxStatuses(Object.fromEntries(items.map(item => [item.msgId, OUTBOX_STATUS.QUEUED]))));
    dispatch(flushOutbox());
};

/**
 * Puts a failed message back in the queue under the same message id.
 *
 * @param {string} msgId Id of the failed message.
 */
export const retryOutboxMessage = msgId => async dispatch => {
    const item = await getItem(OUTBOX_STORE, msgId);
    if (!item || dispatch(refuseIfSendBlocked(item.params?.toJid))) return;
    await setItem(OUTBOX_STORE, msgId, { ...item, status: OUTBOX_STATUS.QUEUED });
    dispatch(setOutboxStatus({ msgId, status: OUTBOX_STATUS.QUEUED }));
    dispatch(flushOutbox());
};

/**
 * Records a message the server refused after accepting it, so it shows "Failed to send" with Retry.
 *
 * @param {object} item { msgId, kind: OUTBOX_KIND, params } able to resend the message.
 */
export const markOutboxFailed = item => async dispatch => {
    await markFailed(dispatch, { ...item, seq: 0, createdAt: Date.now() });
};

/**
 * Restores the queue into the store after a reload or reconnect, then sends what it can.
 * Runs after the SDK has replayed its own offline queue on connect.
 */
export const restoreOutbox = () => async dispatch => {
    const items = [];
    for (const item of await getAllItems(OUTBOX_STORE)) {
        if (item.status !== OUTBOX_STATUS.HANDED_OFF) {
            items.push(item);
        } else if (handedOffMsgIds.delete(item.msgId)) {
            // The SDK sent it on connect; delivery now shows on msgStatus.
            await removeItem(OUTBOX_STORE, item.msgId);
            dispatch(removeFromOutbox(item.msgId));
        } else {
            // Handed off before a reload, so the SDK queue that held it is gone.
            const requeued = { ...item, status: OUTBOX_STATUS.QUEUED };
            await setItem(OUTBOX_STORE, item.msgId, requeued);
            items.push(requeued);
        }
    }
    if (items.length) {
        dispatch(setOutboxStatuses(Object.fromEntries(items.map(item => [item.msgId, item.status]))));
    }
    dispatch(flushOutbox());
};

/**
 * Stops the pending poll retry and resets its backoff, e.g. on logout.
 */
export const cancelOutboxRetry = () => {
    clearTimeout(pollRetryTimer);
    pollRetryTimer = null;
    pollRetryDelayMs = 0;
};

const byQueueOrder = (a, b) => a.createdAt - b.createdAt || a.seq - b.seq;

// Polls go over REST, which the SDK never queues: a network error is no hand-off.
const isPollRetriable = (item, response) =>
    item.kind === OUTBOX_KIND.POLL &&
    (response?.message === SDK_QUEUED_MESSAGE || response?.statusCode === SDK_TIMEOUT_STATUS);

// Nothing else flushes while the link stays up, so a failed poll call retries on a timer.
const schedulePollRetry = dispatch => {
    if (pollRetryTimer || getConnectionStatus() !== CONNECTED) return;
    pollRetryDelayMs = Math.min(pollRetryDelayMs * 2 || POLL_RETRY_BASE_MS, POLL_RETRY_MAX_MS);
    pollRetryTimer = setTimeout(() => {
        pollRetryTimer = null;
        if (getConnectionStatus() === CONNECTED) dispatch(flushOutbox());
    }, pollRetryDelayMs);
};

const markFailed = async (dispatch, item) => {
    await setItem(OUTBOX_STORE, item.msgId, { ...item, status: OUTBOX_STATUS.FAILED });
    dispatch(setOutboxStatus({ msgId: item.msgId, status: OUTBOX_STATUS.FAILED }));
};

const sendQueuedMessages = async dispatch => {
    const items = (await getAllItems(OUTBOX_STORE))
        .filter(item => item.status === OUTBOX_STATUS.QUEUED)
        .sort(byQueueOrder);

    for (const item of items) {
        if (getConnectionStatus() !== CONNECTED) return;
        const response = await sendItem(item);
        if (response?.statusCode === 200) {
            await removeItem(OUTBOX_STORE, item.msgId);
            dispatch(removeFromOutbox(item.msgId));
            if (item.kind === OUTBOX_KIND.POLL) {
                cancelOutboxRetry();
                dispatch(markPollSent(item, response));
            }
        } else if (isPollRetriable(item, response)) {
            // Kept queued for the next flush; the server ignores a repeated messageId.
            schedulePollRetry(dispatch);
            return;
        } else if (item.kind === OUTBOX_KIND.POLL && response?.message === POLL_ERROR.DEADLINE_TOO_SOON) {
            // The deadline passed while queued, so a resend can never succeed.
            await markFailed(dispatch, item);
            toast.error(i18n.t('chat.poll.ERROR_DEADLINE_TOO_SOON'));
        } else if (response?.message === SDK_QUEUED_MESSAGE) {
            // The SDK resends it itself; sending it again here would deliver it twice.
            handedOffMsgIds.add(item.msgId);
            await setItem(OUTBOX_STORE, item.msgId, { ...item, status: OUTBOX_STATUS.HANDED_OFF });
            dispatch(setOutboxStatus({ msgId: item.msgId, status: OUTBOX_STATUS.HANDED_OFF }));
            return;
        } else if (response?.retriable) {
            return;
        } else if (getConnectionStatus() === CONNECTED) {
            // Only a refusal while connected is a failure; a dropped link keeps it queued.
            logger.error('Outbox send failed', {
                msgId: item.msgId,
                kind: item.kind,
                statusCode: response?.statusCode,
            });
            await markFailed(dispatch, item);
        } else {
            return;
        }
    }
};

const sendItem = async item => {
    try {
        if (item.kind === OUTBOX_KIND.CONTACT) return await SDK.sendContactMessage(item.params);
        if (item.kind === OUTBOX_KIND.POLL) return await SDK.createPoll(item.params);
        return await SDK.sendTextMessage(item.params);
    } catch (error) {
        logger.error('Outbox send threw', { msgId: item.msgId, error });
        return null;
    }
};
